import { useCallback, useMemo, useState } from "react";
import moment from "moment";
import { useIntl } from "react-intl";
import { Notification } from "@rio-cloud/rio-uikit";
import type { TransactionImportType } from "@types";
import { useT } from "@hooks";
import { getTransactionIds } from "@services/form";
import type { SharedTableSelection, SharedVehicleRow } from "@components/Table/SharedTable";
import type { SelectionBarProps } from "@components/Table/SelectionBar";
import { toBackendStatus } from "@utils/transactionRecurrence";
import { parseCurrencyBRL } from "@utils";
import type { DashboardFilter } from "../../types/dashboard";
import { resolveMomentLocale } from "./transactionScreenUtils";
import { useBatchSelection, type BatchSelectionItem } from "./useBatchSelection";
import type { TransactionListOriginFilters } from "./useTransactionListData";

type Row = SharedVehicleRow<string>;

interface Params {
  type: TransactionImportType;
  rows: Row[];
  totalRecords: number;
  startDate: string;
  endDate: string;
  search: string;
  listFilters: DashboardFilter;
  originFilters: TransactionListOriginFilters;
  onDeleted: () => void;
  canBatchDelete: boolean;
}

export const toSelectionItem = (row: Row): BatchSelectionItem => ({
  id: row.id,
  version: row.version ?? null,
  recurrenceId: row.recurrenceId ?? null,
  status: toBackendStatus(row.status) ?? "FORECASTED",
  amount: row.amountValue ?? parseCurrencyBRL(row.amount),
  date: row.date,
  category: row.category,
  subcategory: row.subcategory,
  totalInstallments: row.totalInstallments ?? null,
});

const isRowSelectable = (row: Pick<Row, "deletable">) => row.deletable !== false;

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export const useTransactionBatchDelete = ({
  type,
  rows,
  totalRecords,
  startDate,
  endDate,
  search,
  listFilters,
  originFilters,
  onDeleted,
  canBatchDelete,
}: Params) => {
  const t = useT();
  const { locale } = useIntl();
  const [showDialog, setShowDialog] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  const resetKey = useMemo(
    () => JSON.stringify({ startDate, endDate, search, listFilters, originFilters }),
    [startDate, endDate, search, listFilters, originFilters]
  );

  const selection = useBatchSelection(resetKey);
  const { isSelected, toggle, setMany, replaceWithFilter, clear } = selection;

  const rowsById = useMemo(() => new Map(rows.map((row) => [row.id, row])), [rows]);
  const selectableRows = useMemo(() => rows.filter(isRowSelectable), [rows]);
  const selectedLoadedCount = selectableRows.filter((row) => isSelected(row.id)).length;

  const headerState = useMemo(() => {
    if (selectedLoadedCount === 0) return "none" as const;
    return selectedLoadedCount === selectableRows.length ? ("all" as const) : ("some" as const);
  }, [selectableRows.length, selectedLoadedCount]);

  const tableSelection: SharedTableSelection<Row> = {
    isSelected: (row) => isSelected(row.id),
    isSelectable: isRowSelectable,
    onToggle: (row) => toggle(toSelectionItem(rowsById.get(row.id) ?? row)),
    headerState,
    headerDisabled: selectableRows.length === 0,
    onToggleAll: () => setMany(selectableRows.map(toSelectionItem), headerState !== "all"),
    disabledTooltip: t("batchDelete.selection.notDeletable"),
  };

  const selectAllFromFilter = useCallback(async () => {
    setIsSelectingAll(true);
    try {
      const result = await getTransactionIds(
        startDate,
        endDate,
        type,
        { searchTerm: search, origins: originFilters.origins, importIds: originFilters.importIds },
        listFilters
      );

      if (result.limitExceeded) {
        Notification.warning(t("batchDelete.error.limit"));
        return;
      }

      if (result.items.length === 0) {
        Notification.info(t("batchDelete.selection.noneDeletable"));
        return;
      }

      replaceWithFilter(
        result.items.map((item) => {
          const row = rowsById.get(item.id);
          return {
            ...item,
            date: row?.date ?? null,
            category: row?.category ?? null,
            subcategory: row?.subcategory ?? null,
            totalInstallments: row?.totalInstallments ?? null,
          };
        })
      );
    } catch (error) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      Notification.error(detail || t("batchDelete.error.generic"));
    } finally {
      setIsSelectingAll(false);
    }
  }, [endDate, listFilters, originFilters, replaceWithFilter, rowsById, search, startDate, t, type]);

  const isFilterSelection = selection.source === "filter";
  const hasMoreThanLoaded = totalRecords > rows.length;

  const selectionBar: SelectionBarProps = {
    count: selection.count,
    totalAmount: selection.totalAmount,
    isFilterSelection,
    selectAllFromFilterTotal: headerState === "all" && hasMoreThanLoaded ? totalRecords : null,
    isSelectingAllFromFilter: isSelectingAll,
    onSelectAllFromFilter: selectAllFromFilter,
    onClear: clear,
    onDelete: () => setShowDialog(true),
  };

  const periodLabel = useMemo(() => {
    const start = moment(startDate, "YYYY-MM-DD").locale(resolveMomentLocale(locale));
    const end = moment(endDate, "YYYY-MM-DD");

    if (start.isSame(end, "month")) {
      return t("batchDelete.confirm.period.month", {
        month: capitalize(start.format("MMMM")),
        year: start.format("YYYY"),
      });
    }

    return t("batchDelete.confirm.period.range", {
      start: start.format("DD/MM/YYYY"),
      end: end.format("DD/MM/YYYY"),
    });
  }, [endDate, locale, startDate, t]);

  const dialog = {
    show: canBatchDelete && showDialog,
    module: type,
    items: selection.selectedItems,
    isFilterSelection,
    periodLabel,
    onCancel: () => setShowDialog(false),
    onFinished: ({ keepSelection }: { keepSelection: boolean }) => {
      setShowDialog(false);
      if (!keepSelection) clear();
      onDeleted();
    },
  };

  return {
    tableSelection: canBatchDelete ? tableSelection : undefined,
    selectionBar: canBatchDelete ? selectionBar : undefined,
    dialog,
  };
};
